/**
 * The shape somebody draws by hand, and the four gestures that change it.
 *
 * Pure geometry in millimetres — no React, no SVG, no screen coordinates.
 * The rule this module exists to keep (design 2026-08-15 §4.1, and the working
 * rule from 10.08.2026): the drawing surface holds points and shows them, and
 * every statement about the shape is made here, where it can be tested without
 * a browser.
 *
 * Two things it deliberately does **not** do:
 *
 * - It never turns a drawn distance into a measurement. A wall's `lengthMm` is
 *   what somebody typed, and `null` until they do. What the mouse produced is
 *   in the coordinates, and the backend decides what that is worth
 *   (`length_source: sketch`, drawn but not claimed).
 * - It never straightens anything. Every gesture is built so the result is
 *   axis-parallel by construction, so there is nothing to straighten
 *   afterwards.
 */

import { chamferProblem } from "./fase";

export interface DraftVertex {
  xMm: number;
  yMm: number;
}

export interface DraftWall {
  /** What somebody typed for this wall. `null` while nobody has. */
  lengthMm: number | null;
  /** Free text at the wall — „Haustüre", „Bodentreppe". Never geometry. */
  label: string | null;
  /**
   * What the reading had for this wall, where the editor was opened from one
   * (plan 4 §5, `/umriss?aus=lesung`). **A mark, not a source:** the wall
   * shows „gelesen“ while `lengthMm` still equals it, and nothing is sent
   * from it — the editor writes only what a person may stand for (D-1).
   * Every gesture that rebuilds a wall drops it, by the same rule that drops
   * `lengthMm`.
   */
  readMm?: number | null;
  /**
   * The Stand's wall this one continues, where the editor was opened from a
   * Stand („Form ändern …“, `/umriss?aus=stand`, spec 2026-10-15 §3) — sent
   * back as `fromWallId`, so the server can tell a wall kept from a wall made
   * here. **On the wall, not by index:** a corner pulled in at wall 1 moves
   * every wall after it two places on, and a list of ids by position would
   * hand wall 3's figure to wall 5.
   *
   * A name, not a claim — the server alone decides whether the figure keeps
   * its source (unchanged direction and length). So it goes wherever a
   * gesture changes what a wall is: both pieces of a split, a wall two walls
   * became, **and a neighbour a push stretched** (review ruling, fix round 1).
   * Kept there, the old figure typed back would come back `READ` on the
   * server while this screen says „von dir“ — „nie still READ“ (spec §3).
   * Only a wall no gesture has touched in length keeps it. Absent on every
   * other way into the editor.
   */
  fromWallId?: string | null;
}

/**
 * A cut-off corner (Fase), as the draft holds it: two whole-millimetre legs
 * on one corner, keyed by **position** — `wallIndex` is the wall whose
 * **end** corner is cut, so on a ring the last wall's chamfer sits on the
 * seam, between the last wall and the first.
 *
 * The legs are measured from the imagined corner point, the way the customer
 * measured the walls: `legAMm` on wall `wallIndex`, back from its end;
 * `legBMm` on the next wall, forward from its start. They are the whole
 * truth of a chamfer; the slant's length and angle are derived (`fase.ts`).
 */
export interface DraftChamfer {
  wallIndex: number;
  legAMm: number;
  legBMm: number;
}

export interface Draft {
  /**
   * The walk, in drawing order. A closed draft carries its start corner
   * twice: `vertices.at(-1)` is a second copy of `vertices[0]`, the same
   * convention `OutlineCandidate` uses, so a ring and a chain can be walked
   * by one loop.
   */
  vertices: DraftVertex[];
  /** One per step of the walk — always `vertices.length - 1` long. */
  walls: DraftWall[];
  closed: boolean;
  /**
   * The chamfers, at most one per corner. Absent means none — a draft that
   * never had one is byte-for-byte what it was before chamfers existed.
   *
   * **Indexed by wall, like everything else here**, so every gesture that
   * changes which wall sits at which index has to say what happens to them:
   * the ones that rebuild walls (`pullCorner`, `removeCorner`, `deleteWall`)
   * drop them all, `pushWall` carries over those it did not touch.
   */
  chamfers?: DraftChamfer[];
}

/**
 * What a facade wall may plausibly be, in millimetres.
 *
 * A band, not a formality: „200" in a field labelled „m" is a wall two hundred
 * metres long, and „0,05" is five centimetres. Both are a typo or a wrong
 * unit, and either would land on the drawing looking like somebody meant it.
 * The same argument MK-02 makes for a reading, applied where a person types.
 */
const MIN_WALL_MM = 100;
const MAX_WALL_MM = 100_000;

/**
 * A length in metres, as somebody types it, in millimetres — or `null` if that
 * is not a length.
 *
 * Comma and full stop both mean the decimal point here. The ambiguity
 * `core/units.py` has to resolve (a dot with exactly three decimals might be a
 * thousands separator, MK-02) cannot arise in a field labelled „m": nobody
 * writes a house wall in thousands of metres. This is why the parse lives here
 * and not in `format.ts`, whose own header promises it parses nothing.
 */
export function parseMetres(text: string): number | null {
  const trimmed = text.trim().replace(",", ".");
  if (trimmed === "" || !/^\d*\.?\d+$/.test(trimmed)) return null;

  const mm = Math.round(Number(trimmed) * 1000);
  if (!Number.isFinite(mm) || mm < MIN_WALL_MM || mm > MAX_WALL_MM) return null;
  return mm;
}

/**
 * The starting rectangle, from the two numbers typed at the Eingang.
 *
 * Walks clockwise from the top left: top, right, bottom, left.
 *
 * **No wall carries a figure.** The two numbers describe the *bounding box*
 * somebody eyeballed off a sketch — how wide and how deep the house is — and
 * that is not a statement about any one wall: pull the first corner in and no
 * wall is 10.200 long any more while the house still is. An earlier version
 * put them on the first two walls, and the first corner drag then wiped both
 * at once, because those two walls are exactly the ones that corner splits
 * (measured in the browser, 15.08.2026).
 *
 * So the seed sets the **proportion** and nothing else, which is also the
 * order the screen asks for out loud: first the form, then the figures.
 */
export function seedRectangle(widthMm: number, depthMm: number): Draft {
  return {
    vertices: [
      { xMm: 0, yMm: 0 },
      { xMm: widthMm, yMm: 0 },
      { xMm: widthMm, yMm: depthMm },
      { xMm: 0, yMm: depthMm },
      { xMm: 0, yMm: 0 },
    ],
    walls: Array.from({ length: 4 }, () => ({ lengthMm: null, label: null })),
    closed: true,
  };
}

/**
 * The two forms a drawing by hand starts from (Task 17): one wall, or the
 * rectangle above.
 */
export type StartShape = "wall" | "rectangle";

/**
 * One wall, open at both ends — the terrace along one house wall, the most
 * frequent request (UX-Gutachten 23.10.2026). Before it, that request took a
 * rectangle, one figure, „Übernehmen“, „Form ändern …“ and three times
 * „Wand entfernen“; otherwise three walls nobody measured held the release.
 *
 * Like the rectangle it carries no figure: the length is typed, never
 * dragged (E-14). Where the terrace turns a corner, its loose ends carry the
 * walk on („Wand anhängen“, `appendWall`).
 */
export function seedWall(lengthMm: number): Draft {
  return {
    vertices: [
      { xMm: 0, yMm: 0 },
      { xMm: lengthMm, yMm: 0 },
    ],
    walls: [{ lengthMm: null, label: null }],
    closed: false,
  };
}

/**
 * Which of the two starts a drawing's first step is — what „Von vorn“ goes
 * back to — or `null` for any other first step (a reading's outline, a
 * Stand's form).
 */
export function startShapeOf(draft: Draft | undefined): StartShape | null {
  if (draft === undefined) return null;
  if (!draft.closed && draft.walls.length === 1) return "wall";
  if (draft.closed && draft.walls.length === 4) return "rectangle";
  return null;
}

/**
 * The same ring, walked from a different corner.
 *
 * A closed walk has a seam — the corner it starts and ends at — and that seam
 * is an artefact of how the walk was written down, not a property of the
 * shape. `pullCorner` uses this to move the seam out of its way, so it never
 * has to special-case the one corner that is stored twice.
 */
function rotateRing(draft: Draft, by: number): Draft {
  const wallCount = draft.walls.length;
  const corners = draft.vertices.slice(0, wallCount);
  const start = ((by % wallCount) + wallCount) % wallCount;

  const rotated = corners.map((_, index) => corners[(start + index) % wallCount]!);
  const ring: Draft = {
    vertices: [...rotated, rotated[0]!],
    walls: draft.walls.map((_, index) => draft.walls[(start + index) % wallCount]!),
    closed: true,
  };
  // A chamfer goes with its walls: wall `start` becomes wall 0.
  if (draft.chamfers !== undefined && draft.chamfers.length > 0) {
    ring.chamfers = draft.chamfers.map((chamfer) => ({
      ...chamfer,
      wallIndex: (chamfer.wallIndex - start + wallCount) % wallCount,
    }));
  }
  return ring;
}

function isFlat(from: DraftVertex, to: DraftVertex): boolean {
  return from.yMm === to.yMm;
}

function isSameSpot(a: DraftVertex, b: DraftVertex): boolean {
  return a.xMm === b.xMm && a.yMm === b.yMm;
}

/**
 * Pull one corner to a new place, and let a notch grow where it used to be.
 *
 * This is the gesture the whole editor is built around: it is the only one
 * that adds walls, and it is what „die Ecken mit einzeichnen" means. Two walls
 * meet at the corner; afterwards four walls walk around the point that was
 * dragged, and every one of them is axis-parallel **by construction** — the
 * corner is not moved and then straightened, it is replaced by a staircase
 * that was never crooked. Dragged inwards it is a Rücksprung, outwards a
 * Vorsprung; that is one gesture with two signs, not two gestures.
 *
 * Both split walls lose their `lengthMm`: each is now shorter than the number
 * somebody typed for it, so the number has stopped describing it. Carrying it
 * over to the longer piece would be a guess, and guessing is what E-16
 * forbids. The `label` does survive on the remaining piece — a note about a
 * place is not a claim about a length.
 *
 * Returns the draft untouched when the pull cannot produce a notch: a move
 * along one axis only (that is a wall push), a corner that is really a
 * straight joint, or a target that would collapse one of the four walls to
 * nothing.
 */
export function pullCorner(draft: Draft, vertexIndex: number, to: DraftVertex): Draft {
  const wallCount = draft.walls.length;

  // The seam corner is stored twice; walk the ring from somewhere else so it
  // has an incoming and an outgoing wall like every other corner.
  if (draft.closed && (vertexIndex === 0 || vertexIndex === wallCount)) {
    return pullCorner(rotateRing(draft, 1), wallCount - 1, to);
  }
  if (vertexIndex <= 0 || vertexIndex >= draft.vertices.length - 1) {
    return draft; // an open chain's loose end has no corner to notch
  }

  const before = draft.vertices[vertexIndex - 1]!;
  const corner = draft.vertices[vertexIndex]!;
  const after = draft.vertices[vertexIndex + 1]!;

  const incomingFlat = isFlat(before, corner);
  if (incomingFlat === isFlat(corner, after)) {
    return draft; // a straight joint, not a corner
  }

  const [first, middle, last] = incomingFlat
    ? [
        { xMm: to.xMm, yMm: corner.yMm },
        { xMm: to.xMm, yMm: to.yMm },
        { xMm: corner.xMm, yMm: to.yMm },
      ]
    : [
        { xMm: corner.xMm, yMm: to.yMm },
        { xMm: to.xMm, yMm: to.yMm },
        { xMm: to.xMm, yMm: corner.yMm },
      ];

  const walk = [before, first, middle, last, after];
  for (let step = 0; step + 1 < walk.length; step += 1) {
    if (isSameSpot(walk[step]!, walk[step + 1]!)) {
      return draft; // one of the four walls would have no length
    }
  }

  const incoming = draft.walls[vertexIndex - 1]!;
  const outgoing = draft.walls[vertexIndex]!;

  return {
    vertices: [
      ...draft.vertices.slice(0, vertexIndex),
      first,
      middle,
      last,
      ...draft.vertices.slice(vertexIndex + 1),
    ],
    walls: [
      ...draft.walls.slice(0, vertexIndex - 1),
      { lengthMm: null, label: incoming.label },
      { lengthMm: null, label: null },
      { lengthMm: null, label: null },
      { lengthMm: null, label: outgoing.label },
      ...draft.walls.slice(vertexIndex + 1),
    ],
    closed: draft.closed,
  };
}

/** A pull shorter than this at a loose end adds nothing — a click is not a wall. */
const APPEND_MIN_MM = 50;
/** How long „Wand anhängen“ makes the new wall; pulled to size afterwards. */
const APPEND_DEFAULT_MM = 1000;

/** Whether `vertexIndex` is a loose end of an open walk. */
function isLooseEnd(draft: Draft, vertexIndex: number): boolean {
  return !draft.closed && (vertexIndex === 0 || vertexIndex === draft.vertices.length - 1);
}

/**
 * Grow an open walk at a loose end: one new wall from the end towards `to`
 * (19.10.2026, Denis — a reading's outline stopped short of the sketch, and
 * nothing in the editor could carry it on).
 *
 * The wall runs along whichever axis the pull is longer on, so it is
 * axis-parallel by construction, like every wall `pullCorner` makes. It comes
 * without a figure — a dragged length is a sketch, not a measure (E-14) —
 * and every wall already there keeps its figure and label untouched.
 *
 * Returns the draft untouched for a ring, an inner corner, a pull shorter than
 * `APPEND_MIN_MM`, or a wall that would fold back over the end wall.
 */
export function appendWall(draft: Draft, vertexIndex: number, to: DraftVertex): Draft {
  if (!isLooseEnd(draft, vertexIndex) || draft.vertices.length < 2) return draft;
  const atStart = vertexIndex === 0;
  const end = draft.vertices[vertexIndex]!;
  const neighbour = draft.vertices[atStart ? 1 : vertexIndex - 1]!;
  const dx = Math.round(to.xMm - end.xMm);
  const dy = Math.round(to.yMm - end.yMm);
  const flat = Math.abs(dx) >= Math.abs(dy);
  const step = flat ? dx : dy;
  if (Math.abs(step) < APPEND_MIN_MM) return draft;
  const endWallFlat = isFlat(neighbour, end);
  if (endWallFlat === flat) {
    // Along the end wall: carrying it on is fine, turning back over it is not.
    const back = flat ? Math.sign(neighbour.xMm - end.xMm) : Math.sign(neighbour.yMm - end.yMm);
    if (back === Math.sign(step)) return draft;
  }
  const tip = flat ? { xMm: end.xMm + step, yMm: end.yMm } : { xMm: end.xMm, yMm: end.yMm + step };
  const wall: DraftWall = { lengthMm: null, label: null };
  return atStart
    ? { ...draft, vertices: [tip, ...draft.vertices], walls: [wall, ...draft.walls] }
    : { ...draft, vertices: [...draft.vertices, tip], walls: [...draft.walls, wall] };
}

/**
 * Where „Wand anhängen“ aims: a metre across the end wall, towards the middle
 * of what is drawn — the keyboard's way to the gesture, as `notchTarget` is
 * for „Ecke einziehen“. `null` anywhere but a loose end of an open walk.
 */
export function appendTarget(draft: Draft, vertexIndex: number): DraftVertex | null {
  if (!isLooseEnd(draft, vertexIndex) || draft.vertices.length < 2) return null;
  const end = draft.vertices[vertexIndex]!;
  const neighbour = draft.vertices[vertexIndex === 0 ? 1 : vertexIndex - 1]!;
  const middle = centreOf(draft.vertices);
  if (isFlat(neighbour, end)) {
    return { xMm: end.xMm, yMm: end.yMm + (Math.sign(middle.yMm - end.yMm) || 1) * APPEND_DEFAULT_MM };
  }
  return { xMm: end.xMm + (Math.sign(middle.xMm - end.xMm) || 1) * APPEND_DEFAULT_MM, yMm: end.yMm };
}

/** How much of each adjoining wall a default notch takes. */
const NOTCH_SHARE = 0.25;

/**
 * Where „Ecke einziehen" aims: a point a quarter of the way along each
 * adjoining wall, into the shape.
 *
 * This exists so the one gesture that adds walls is reachable without a
 * mouse. `OutlineReview` already made the wall drag keyboard-operable on the
 * grounds that "a drag only a mouse can perform is a correction only a mouse
 * user can make", and a corner pull is the same argument. A button aiming at
 * a sensible default, refined afterwards with the wall pushes that already
 * take arrow keys, beats arrow-key dragging on an invisible handle — it is
 * also the only one of the two a person can find.
 *
 * `null` at a loose end of an open walk, which has no corner to notch.
 */
export function notchTarget(draft: Draft, vertexIndex: number): DraftVertex | null {
  const wallCount = draft.walls.length;
  const walk = draft.closed && (vertexIndex === 0 || vertexIndex === wallCount)
    ? { draft: rotateRing(draft, 1), index: wallCount - 1 }
    : { draft, index: vertexIndex };
  if (walk.index <= 0 || walk.index >= walk.draft.vertices.length - 1) return null;

  const before = walk.draft.vertices[walk.index - 1]!;
  const corner = walk.draft.vertices[walk.index]!;
  const after = walk.draft.vertices[walk.index + 1]!;

  // Towards the middle of what is drawn. „Inwards" needs no notion of inside:
  // on any shape this module can produce, the centre of the bounding box is
  // on the side a notch belongs.
  const middle = centreOf(walk.draft.vertices);
  // Exactly one of the two adjoining walls runs along each axis, so the
  // non-zero one of each pair is the length this notch may spend.
  const alongX = Math.abs(corner.xMm - before.xMm) || Math.abs(corner.xMm - after.xMm);
  const alongY = Math.abs(corner.yMm - before.yMm) || Math.abs(corner.yMm - after.yMm);

  return {
    xMm: corner.xMm + Math.sign(middle.xMm - corner.xMm) * Math.round(alongX * NOTCH_SHARE),
    yMm: corner.yMm + Math.sign(middle.yMm - corner.yMm) * Math.round(alongY * NOTCH_SHARE),
  };
}

function centreOf(vertices: DraftVertex[]): DraftVertex {
  const xs = vertices.map((vertex) => vertex.xMm);
  const ys = vertices.map((vertex) => vertex.yMm);
  return {
    xMm: (Math.min(...xs) + Math.max(...xs)) / 2,
    yMm: (Math.min(...ys) + Math.max(...ys)) / 2,
  };
}

/** The wall either side of `wallIndex`, wrapping the ring seam when closed. */
function neighboursOf(draft: Draft, wallIndex: number): number[] {
  const count = draft.walls.length;
  const left = wallIndex > 0 ? wallIndex - 1 : draft.closed && count > 1 ? count - 1 : null;
  const right =
    wallIndex + 1 < count ? wallIndex + 1 : draft.closed && count > 1 ? 0 : null;
  return [left, right].filter((index): index is number => index !== null);
}

/** A figure a gesture would take off a wall: where it stood (zero-based, in the draft before) and what it was. */
export interface LostFigure {
  wallIndex: number;
  lengthMm: number;
  /** Whether it was still the one the reading had („gelesen“) — otherwise a person typed it. */
  read: boolean;
}

/**
 * The figures `before` has that `after` no longer carries — each wall with a
 * length whose counterpart in `after` is gone or says something else. What a
 * gesture takes without saying so (review 2026-10-28, B-4: a wall pushed,
 * and the two beside it stop being 2,42 m and 1,78 m): asked about before it
 * counts, never after (spec §3.4).
 *
 * **The counterpart of a wall** is the wall that carries its name in a Stand
 * (`fromWallId`), where it has one; where it has none, the wall at the same
 * index — if the count did not change, which a push never does outside a
 * flush — and where the count did change (a corner pulled, a step pushed
 * flush) the wall with the same two ends. A figure only counts as kept if its
 * counterpart carries the same length: the wall that was pushed has moved,
 * not changed, and a wall a person stretched is no longer the wall that was
 * measured.
 */
export function figuresLost(before: Draft, after: Draft): LostFigure[] {
  const sameCount = before.walls.length === after.walls.length;
  const counterpartOf = (index: number, wall: DraftWall) => (candidate: DraftWall, at: number): boolean => {
    if (candidate.lengthMm !== wall.lengthMm) return false;
    if (wall.fromWallId != null) return candidate.fromWallId === wall.fromWallId;
    if (sameCount) return at === index;
    return (
      isSameSpot(before.vertices[index]!, after.vertices[at]!) &&
      isSameSpot(before.vertices[index + 1]!, after.vertices[at + 1]!)
    );
  };

  const lost: LostFigure[] = [];
  before.walls.forEach((wall, index) => {
    if (wall.lengthMm === null || after.walls.some(counterpartOf(index, wall))) return;
    lost.push({ wallIndex: index, lengthMm: wall.lengthMm, read: wall.readMm === wall.lengthMm });
  });
  return lost;
}

/** How a push treats a neighbour it would shrink to nothing. */
export interface PushOptions {
  /**
   * Within this many millimetres of flush, the push lands flush — the
   * screen passes a few pixels at the board's scale, so a person who drags
   * a wall onto the line of the one beyond hits it without pixel-hunting.
   * `0` (the default, and every arrow key) lands flush only exactly.
   */
  snapMm?: number;
}

/** A gesture's new shape, and where the wall it held ended up. */
export interface Tracked {
  draft: Draft;
  /** The index of the wall the gesture worked on — merged, it is the merged wall. `null` where it is gone. */
  wall: number | null;
}

/**
 * Slide a wall sideways. Its two corners travel together, its neighbours
 * stretch to keep up, and the wall count does not change — unless a
 * neighbour is pushed **flush**.
 *
 * The pushed wall keeps its own number — it did not get longer or shorter, it
 * moved. Its two neighbours did change length, so whatever was typed for them
 * stops being true and is dropped, by the same rule that governs a split
 * (design §4.2).
 *
 * **Flush (Kauf-Test 11.10.2026, „wie entferne ich eine Ecke?“).** Pushed
 * as far as a neighbour is long — or past it, or to within `snapMm` — the
 * push lands exactly there: the neighbour has no length left, so it is gone,
 * its two corners with it, and the wall pushed and the wall beyond it now run
 * on one line and become one wall (`healAt`). That is how a step comes out:
 * the T-shape's right stem pushed onto the outer edge. Before, the push
 * stopped a millimetre short and left a one-millimetre wall with two corners
 * standing — it looked broken, and it was. A ring that would come out of it
 * with fewer than four walls is refused, as any push past a neighbour was.
 *
 * A ring stores its seam corner twice; both copies move on any push that
 * touches either, or the walk would come apart at a corner while still
 * calling itself closed.
 */
export function pushWall(draft: Draft, wallIndex: number, deltaMm: number, options: PushOptions = {}): Draft {
  return pushWallTracked(draft, wallIndex, deltaMm, options).draft;
}

/** `pushWall`, and where the pushed wall is afterwards — merged, the merged wall. */
export function pushWallTracked(
  draft: Draft,
  wallIndex: number,
  deltaMm: number,
  { snapMm = 0 }: PushOptions = {},
): Tracked {
  const unchanged = { draft, wall: wallIndex };
  const from = draft.vertices[wallIndex];
  const to = draft.vertices[wallIndex + 1];
  if (!from || !to || deltaMm === 0) return unchanged;

  const moving = new Set([wallIndex, wallIndex + 1]);
  const last = draft.vertices.length - 1;
  if (draft.closed && (moving.has(0) || moving.has(last))) {
    moving.add(0);
    moving.add(last);
  }

  const alongX = isFlat(from, to);
  const coordinate = alongX ? "yMm" : "xMm";

  // How long each neighbour is along the push, signed: positive where the
  // push shortens it.
  const extents: number[] = [];
  for (const neighbour of neighboursOf(draft, wallIndex)) {
    const movingEnd = moving.has(neighbour) ? neighbour : neighbour + 1;
    const fixedEnd = movingEnd === neighbour ? neighbour + 1 : neighbour;
    if (moving.has(fixedEnd) || !moving.has(movingEnd)) continue;
    extents.push(draft.vertices[fixedEnd]![coordinate] - draft.vertices[movingEnd]![coordinate]);
  }

  // The first neighbour the push reaches — or comes within `snapMm` of —
  // decides where it stops: exactly flush with it.
  let delta = deltaMm;
  const reached = extents
    .filter((extent) => Math.sign(extent) === Math.sign(deltaMm) && Math.abs(deltaMm) >= Math.abs(extent) - snapMm)
    .sort((a, b) => Math.abs(a) - Math.abs(b))[0];
  if (reached !== undefined) delta = reached;

  // Every neighbour has to survive the move with its length *and its sign*,
  // or go to exactly nothing. Checking the signed extent rather than its
  // magnitude is what stops the shape turning inside out: a push far enough
  // to drag a wall clean through its neighbour comes out the other side
  // reading as a plausible positive length again (backend
  // `_clamp_for_neighbour` makes the same argument).
  for (const extent of extents) {
    const after = extent - delta;
    if (after !== 0 && (extent >= 0 ? after < 1 : after > -1)) return unchanged;
  }

  const vertices = draft.vertices.map((vertex, index) =>
    moving.has(index)
      ? alongX
        ? { xMm: vertex.xMm, yMm: vertex.yMm + delta }
        : { xMm: vertex.xMm + delta, yMm: vertex.yMm }
      : vertex,
  );

  const stretched = new Set(neighboursOf(draft, wallIndex));
  // A stretched neighbour loses its figure, the „gelesen“ that rides on it,
  // and its name in a Stand (`fromWallId`): its length is no longer the one
  // anybody stated, and whatever is typed for it now is a person's.
  const walls = draft.walls.map((wall, index) =>
    stretched.has(index) ? { lengthMm: null, label: wall.label } : wall,
  );
  const pushed = { vertices, walls, closed: draft.closed };
  if (!walls.some((_, index) => isSameSpot(vertices[index]!, vertices[index + 1]!))) {
    return { draft: keepChamfers(draft, pushed), wall: wallIndex };
  }

  const walk = walkOf(pushed, walls[wallIndex]!);
  for (let zero = zeroWall(walk); zero !== null; zero = zeroWall(walk)) {
    const junction = dropWall(walk, zero);
    if (junction === undefined) return unchanged;
    if (junction !== null) healAt(walk, junction);
  }
  const done = finished(walk);
  return done === null ? unchanged : { draft: keepChamfers(draft, done.draft), wall: done.wall };
}

/**
 * `after` — a draft a push just built — carrying those of `before`'s
 * chamfers that are still on a corner and still allowed on it.
 *
 * **By corner position, not by index.** A flush push takes walls out and
 * every index after them moves on; a chamfer kept at its old index would
 * silently sit on another corner. The corners a push does not move keep their
 * coordinates, so that is what finds a chamfer again. The two corners of the
 * pushed wall move, and its neighbours lose their figures — a chamfer on
 * either fails `chamferProblem` ("braucht ein Maß") and goes, which is the
 * rule: a chamfer is the customer's cut at a corner whose walls somebody
 * measured, and the push just took that measure away.
 */
function keepChamfers(before: Draft, after: Draft): Draft {
  const found: DraftChamfer[] = [];
  for (const chamfer of before.chamfers ?? []) {
    const corner = before.vertices[chamfer.wallIndex + 1];
    if (corner === undefined) continue;
    const wallIndex = after.walls.findIndex((_, wall) => isSameSpot(after.vertices[wall + 1]!, corner));
    if (wallIndex !== -1) found.push({ ...chamfer, wallIndex });
  }
  const candidate: Draft = { ...after, chamfers: found };
  const standing = found.filter((chamfer) => chamferProblem(candidate, chamfer) === null);
  return standing.length > 0 ? { ...after, chamfers: standing } : after;
}

/**
 * Take a corner out, where the two walls at it run on one line (Kauf-Test
 * 11.10.2026): the two become one wall, its figure the sum of theirs where
 * both have one. A corner where the walk turns cannot just go — the walls
 * would no longer meet; that is a step, and a step comes out by pushing a
 * wall flush (`pushWall`). Refused, with the reason, by `cornerRemoval`.
 */
export function removeCorner(draft: Draft, vertexIndex: number): Tracked {
  const unchanged = { draft, wall: null };
  if (cornerRemoval(draft, vertexIndex) !== "ok") return unchanged;
  const walk = walkOf(draft, null);
  const corner = draft.closed && vertexIndex === walk.walls.length ? 0 : vertexIndex;
  walk.tracked = walk.walls[(corner - 1 + walk.walls.length) % walk.walls.length]!;
  const next = joinWalls(walk, corner);
  if (next !== null) healAt(walk, next);
  return finished(walk) ?? unchanged;
}

/**
 * Whether a corner can go, and if not, why: `"end"` at the loose end of an
 * open walk (one wall, nothing to join), `"turns"` where the walk turns,
 * `"tooFew"` where a ring would fall under four walls.
 */
export function cornerRemoval(draft: Draft, vertexIndex: number): "ok" | "end" | "turns" | "tooFew" {
  const count = draft.walls.length;
  if (draft.closed) {
    if (vertexIndex < 0 || vertexIndex > count) return "turns";
  } else if (vertexIndex <= 0 || vertexIndex >= count) {
    return "end";
  }
  const corner = draft.closed && vertexIndex === count ? 0 : vertexIndex;
  const walk = walkOf(draft, null);
  const before = (corner - 1 + count) % count;
  if (!oneLine(walk, before, corner % count)) return "turns";
  const trial = walkOf(draft, null);
  const next = joinWalls(trial, corner);
  if (next !== null) healAt(trial, next);
  return finished(trial) === null ? "tooFew" : "ok";
}

// ── The walk as a cycle, for the gestures that take walls out ──────────────

/**
 * A draft with its seam corner named once: on a ring, wall `k` runs from
 * corner `k` to corner `k + 1` modulo the count; on an open walk there is one
 * corner more than walls. `tracked` is the wall a gesture holds, followed
 * through every merge.
 */
interface Walk {
  corners: DraftVertex[];
  walls: DraftWall[];
  closed: boolean;
  tracked: DraftWall | null;
}

function walkOf(draft: Draft, tracked: DraftWall | null): Walk {
  return {
    corners: draft.closed ? draft.vertices.slice(0, -1) : draft.vertices.slice(),
    walls: draft.walls.slice(),
    closed: draft.closed,
    tracked,
  };
}

/** Back to a draft — or `null` where too little is left: a ring under four walls, a walk without one. */
function finished(walk: Walk): Tracked | null {
  if (walk.closed ? walk.walls.length < 4 : walk.walls.length < 1) return null;
  if (zeroWall(walk) !== null) return null;
  const index = walk.tracked === null ? -1 : walk.walls.indexOf(walk.tracked);
  return {
    draft: {
      vertices: walk.closed ? [...walk.corners, walk.corners[0]!] : walk.corners,
      walls: walk.walls,
      closed: walk.closed,
    },
    wall: index === -1 ? null : index,
  };
}

function endsOf(walk: Walk, wall: number): [DraftVertex, DraftVertex] {
  return [walk.corners[wall]!, walk.corners[(wall + 1) % walk.corners.length]!];
}

function zeroWall(walk: Walk): number | null {
  const index = walk.walls.findIndex((_, wall) => isSameSpot(...endsOf(walk, wall)));
  return index === -1 ? null : index;
}

/** Two walls that run on one axis — at a shared corner, on one line. */
function oneLine(walk: Walk, a: number, b: number): boolean {
  if (a === b) return false;
  const [a0, a1] = endsOf(walk, a);
  const [b0, b1] = endsOf(walk, b);
  return isFlat(a0, a1) === isFlat(b0, b1);
}

/**
 * Take out a wall that has no length: its two corners are one. Returns the
 * corner where its neighbours now meet, `null` at the loose end of an open
 * walk (nothing meets there), `undefined` where the walk would be empty.
 */
function dropWall(walk: Walk, wall: number): number | null | undefined {
  const count = walk.walls.length;
  if (walk.closed) {
    walk.walls.splice(wall, 1);
    if (wall === count - 1) {
      walk.corners.splice(wall, 1);
      return 0;
    }
    walk.corners.splice(wall + 1, 1);
    return wall;
  }
  if (count <= 1) return undefined;
  if (wall === 0) {
    walk.walls.splice(0, 1);
    walk.corners.splice(0, 1);
    return null;
  }
  walk.walls.splice(wall, 1);
  walk.corners.splice(wall + 1, 1);
  return wall === count - 1 ? null : wall;
}

/** `a · b` — two notes about places that are now one wall; one if they were the same. */
function joinedLabel(a: string | null, b: string | null): string | null {
  if (a === null || b === null || a === b) return a ?? b;
  return `${a} · ${b}`;
}

/**
 * Join the two walls at a corner into one and take the corner out. Its
 * figure is the sum of theirs where both have one, signed by which way each
 * runs — a typed sum, never a drawn length (A-014) — and `null` where either
 * has none, or where the sum would contradict the drawn direction. „gelesen“
 * does not survive: the figure is derived, not read.
 *
 * Returns the corner to look at next — the merged wall's start, where it has
 * no length (a notch pushed shut: down and back up on one line) — or `null`.
 */
function joinWalls(walk: Walk, corner: number): number | null {
  const count = walk.walls.length;
  const before = walk.closed ? (corner - 1 + count) % count : corner - 1;
  const after = walk.closed ? corner % count : corner;
  const [start] = endsOf(walk, before);
  const [, end] = endsOf(walk, after);
  const coordinate = isFlat(...endsOf(walk, before)) ? "xMm" : "yMm";
  const signOf = (wall: number) => {
    const [from, to] = endsOf(walk, wall);
    return Math.sign(to[coordinate] - from[coordinate]);
  };
  const a = walk.walls[before]!;
  const b = walk.walls[after]!;
  const drawn = Math.sign(end[coordinate] - start[coordinate]);
  const typed =
    a.lengthMm !== null && b.lengthMm !== null ? signOf(before) * a.lengthMm + signOf(after) * b.lengthMm : null;
  const merged: DraftWall = {
    lengthMm: typed !== null && typed !== 0 && Math.sign(typed) === drawn ? Math.abs(typed) : null,
    label: joinedLabel(a.label, b.label),
  };
  if (walk.tracked === a || walk.tracked === b) walk.tracked = merged;

  if (walk.closed && corner % count === 0) {
    // The seam: wall 0 goes, the last wall becomes the merged one, and the
    // ring starts at what was corner 1.
    walk.corners.splice(0, 1);
    walk.walls.splice(0, 1);
    walk.walls[walk.walls.length - 1] = merged;
    const index = walk.walls.length - 1;
    return isSameSpot(...endsOf(walk, index)) ? index : null;
  }
  walk.corners.splice(corner, 1);
  walk.walls.splice(before, 2, merged);
  return isSameSpot(...endsOf(walk, before)) ? before : null;
}

/**
 * Where two walls now meet that may run on one line: join them, and keep
 * going while a join leaves a wall of no length behind. Only here — a split
 * facade elsewhere in the drawing (two walls on one line, each with its own
 * figure) is somebody's statement and stays as it is.
 */
function healAt(walk: Walk, corner: number): void {
  let at: number | null = corner;
  for (let guard = 0; at !== null && guard < 64; guard += 1) {
    const count = walk.walls.length;
    if (count === 0) return;
    const current: number = walk.closed ? at % count : at;
    const outgoing = walk.closed || current < count ? current : null;
    const incoming = walk.closed ? (current - 1 + count) % count : current > 0 ? current - 1 : null;
    if (outgoing !== null && isSameSpot(...endsOf(walk, outgoing))) {
      at = dropWall(walk, outgoing) ?? null;
    } else if (incoming !== null && incoming !== outgoing && isSameSpot(...endsOf(walk, incoming))) {
      at = dropWall(walk, incoming) ?? null;
    } else {
      at = incoming !== null && outgoing !== null && oneLine(walk, incoming, outgoing) ? joinWalls(walk, current) : null;
    }
  }
}

/**
 * Take a wall out of the walk.
 *
 * On a ring this is how „hier hört die Zeichnung auf" is said: the ring opens
 * where the wall was, and the walk restarts at that wall's far corner. `L4`
 * needs exactly this — the photographed sketch is cut off on the right, so its
 * contour is open, and a separate „geschlossen"-Schalter would only say the
 * same thing less directly.
 *
 * On an already-open walk only a loose end may go. Removing a wall from the
 * middle would leave two chains, and this is one drawing.
 */
export function deleteWall(draft: Draft, wallIndex: number): Draft {
  const count = draft.walls.length;
  if (wallIndex < 0 || wallIndex >= count || count <= 1) return draft;

  if (draft.closed) {
    const rotated = rotateRing(draft, wallIndex + 1);
    return {
      vertices: rotated.vertices.slice(0, -1),
      walls: rotated.walls.slice(0, -1),
      closed: false,
    };
  }

  if (wallIndex === 0) {
    return { vertices: draft.vertices.slice(1), walls: draft.walls.slice(1), closed: false };
  }
  if (wallIndex === count - 1) {
    return {
      vertices: draft.vertices.slice(0, -1),
      walls: draft.walls.slice(0, -1),
      closed: false,
    };
  }
  return draft;
}

/**
 * The outline a reading proposed, as a draft the editor can work on (plan 4
 * §5, `/umriss?aus=lesung`) — or `null` where the seed does not describe a
 * walk (a wall count that fits neither convention).
 *
 * A closed seed may list its start corner once or twice; the draft always
 * carries it twice (see `Draft.vertices`). Read lengths stay lengths, and
 * keep their mark: `readMm` is what makes the board say „gelesen“.
 *
 * `typed` puts figures a person already set on the review screen onto their
 * walls by index — the same walls, read from the same reading — so opening
 * the editor does not take back what was done there. The mark stays on the
 * read value, so a wall whose figure a person changed no longer says
 * „gelesen“.
 */
export function draftFromSeed(
  seed: {
    closed: boolean;
    vertices: [number, number][];
    walls: { lengthMm: number | null; label: string | null; seen: "gelesen" | null }[];
  },
  typed: ReadonlyMap<number, number> = new Map(),
): Draft | null {
  const points = seed.vertices.map(([xMm, yMm]) => ({ xMm, yMm }));
  const first = points[0];
  if (first === undefined || seed.walls.length === 0) return null;
  const last = points.at(-1)!;
  const repeated = points.length > 1 && last.xMm === first.xMm && last.yMm === first.yMm;
  const vertices = seed.closed && !repeated ? [...points, first] : points;
  if (vertices.length !== seed.walls.length + 1) return null;

  return {
    vertices,
    walls: seed.walls.map((wall, index) => {
      const read = wall.seen === "gelesen" ? wall.lengthMm : null;
      return { lengthMm: typed.get(index) ?? wall.lengthMm, label: wall.label, readMm: read };
    }),
    closed: seed.closed,
  };
}

/**
 * The Stand's form as a draft („Form ändern …“, spec 2026-10-15 §3) — or
 * `null` where its walls do not fit its corners.
 *
 * Every wall carries the name of the Stand's wall it starts from
 * (`fromWallId`), and a figure read off a drawing carries its mark (`readMm`,
 * „gelesen“) as on the way in from a reading. A typed figure has none — it is
 * „von dir“ already. A sketched wall has no figure, whatever arrives with it:
 * a drawn length never becomes a typed one (A-014).
 */
export function draftFromStand(seed: {
  closed: boolean;
  vertices: [number, number][];
  walls: { wallId: string; lengthMm: number | null; label: string | null; source: "read" | "manual" | "sketch" }[];
  chamfers?: { wallIndex: number; legAMm: number; legBMm: number }[];
}): Draft | null {
  const draft = draftFromSeed({
    closed: seed.closed,
    vertices: seed.vertices,
    walls: seed.walls.map((wall) => {
      const lengthMm = wall.source === "sketch" ? null : wall.lengthMm;
      return {
        lengthMm,
        label: wall.label,
        seen: wall.source === "read" && lengthMm !== null ? ("gelesen" as const) : null,
      };
    }),
  });
  if (draft === null) return null;
  const named: Draft = {
    ...draft,
    walls: draft.walls.map((wall, index) => ({ ...wall, fromWallId: seed.walls[index]!.wallId })),
  };

  // The Stand's chamfers ride along, keyed as the server keys them (the wall
  // whose end corner is cut). A Stand the server accepted cannot carry one
  // that does not stand on these walls — if it does, the seed does not
  // describe a walk, and saying so beats editing a shape that is not there.
  if (seed.chamfers === undefined || seed.chamfers.length === 0) return named;
  const chamfers = seed.chamfers.map(({ wallIndex, legAMm, legBMm }) => ({ wallIndex, legAMm, legBMm }));
  const chamfered: Draft = { ...named, chamfers };
  return chamfers.every((chamfer) => chamferProblem(chamfered, chamfer) === null) ? chamfered : null;
}

/**
 * The walk as the server takes it back — `POST …/outline/from-editor` and
 * `POST …/outline/basis` alike, both through the hand path's solver
 * (`manual_outline`): a closed walk names its start **twice**, so there is
 * always one corner more than walls.
 *
 * It replaces `wireVertices`, which named the start once — and with which
 * `manual_outline` refused every closed outline taken over from a reading
 * („4 Punkte ergeben 3 Wände, übergeben wurden 4“, fix round 1 of Task 7).
 *
 * Whole millimetres on the wire; a drag snaps to 10 mm anyway, this only
 * keeps a calibrated corner from arriving as a fraction.
 */
export function walkVertices(draft: Draft): [number, number][] {
  return draft.vertices.map((vertex) => [Math.round(vertex.xMm), Math.round(vertex.yMm)]);
}

/**
 * How large the sheet is in millimetres, from the same corners given twice —
 * in mm by the editor seed (origin at the sheet's top left, plan 4 §4) and as
 * page fractions by the reading. With it the page can lie under the outline
 * 1:1 (plan 4 §5: „Original als Vorlage dahinter“).
 *
 * `null` where the two do not describe one sheet — too few corners to span
 * it, or an origin that is not the sheet's corner. Then the page stays beside
 * the board instead of under it: an underlay a few centimetres off would
 * invite tracing the wrong line.
 */
export function sheetSize(
  verticesMm: readonly (readonly [number, number])[],
  fractions: readonly (readonly [number, number])[],
): { widthMm: number; heightMm: number } | null {
  if (verticesMm.length < 2 || fractions.length < 2) return null;
  const span = (values: number[]) => [Math.min(...values), Math.max(...values)] as const;
  const [minX, maxX] = span(verticesMm.map(([x]) => x));
  const [minY, maxY] = span(verticesMm.map(([, y]) => y));
  const [minFx, maxFx] = span(fractions.map(([x]) => x));
  const [minFy, maxFy] = span(fractions.map(([, y]) => y));
  if (maxFx - minFx < 1e-3 || maxFy - minFy < 1e-3) return null;
  const widthMm = (maxX - minX) / (maxFx - minFx);
  const heightMm = (maxY - minY) / (maxFy - minFy);
  const offX = Math.abs(minX - minFx * widthMm);
  const offY = Math.abs(minY - minFy * heightMm);
  if (offX > 0.01 * widthMm || offY > 0.01 * heightMm) return null;
  return { widthMm, heightMm };
}
